import { apiClient } from './api'
import type {
  PaymentProof,
  PaymentProofWithRelations,
  SubmitProofInput,
  PaymentProofListResponse,
} from '@/types/paymentProof'

interface ApiResponse<T> {
  success: boolean
  data: T
  error?: {
    code: string
    message: string
  }
  message?: string
}

// Helper: the interceptor strips AxiosResponse, so the return value IS the body.
// Cast to the correct type so TS doesn't complain about .data access.
async function api<T>(promise: Promise<unknown>): Promise<ApiResponse<T>> {
  return promise as unknown as ApiResponse<T>
}

export const paymentProofService = {
  submitProof: async (
    enrollmentId: string,
    input: SubmitProofInput
  ): Promise<PaymentProof> => {
    const response = await api<PaymentProof>(
      apiClient.post(`/payment-proofs/enrollment/${enrollmentId}`, {
        amount_paid: input.amount_paid,
        bank_name: input.bank_name,
        account_number: input.account_number,
        account_holder_name: input.account_holder_name,
        proof_file_url: input.proof_file_url,
        proof_file_type: input.proof_file_type,
        notes: input.notes,
      })
    )
    if (!response.success) {
      throw new Error(response.error?.message || 'Gagal mengupload bukti pembayaran')
    }
    return response.data
  },

  getMyProofs: async (): Promise<PaymentProof[]> => {
    const response = await api<PaymentProof[]>(
      apiClient.get('/payment-proofs/me')
    )
    if (!response.success) {
      throw new Error(response.error?.message || 'Gagal mengambil data bukti pembayaran')
    }
    return response.data || []
  },

  getProofDetail: async (proofId: string): Promise<PaymentProof> => {
    const response = await api<PaymentProof>(
      apiClient.get(`/payment-proofs/${proofId}`)
    )
    if (!response.success) {
      throw new Error(response.error?.message || 'Gagal mengambil detail bukti pembayaran')
    }
    return response.data
  },

  cancelProof: async (proofId: string): Promise<void> => {
    const response = await api<null>(
      apiClient.delete(`/payment-proofs/${proofId}`)
    )
    if (!response.success) {
      throw new Error(response.error?.message || 'Gagal membatalkan bukti pembayaran')
    }
  },

  listVerifications: async (
    status?: string,
    page = 1,
    limit = 20
  ): Promise<PaymentProofListResponse> => {
    const params = new URLSearchParams()
    if (status) params.append('status', status)
    params.append('page', String(page))
    params.append('limit', String(limit))

    const response = await api<PaymentProofListResponse>(
      apiClient.get(`/admin/payment-proofs?${params.toString()}`)
    )
    if (!response.success) {
      throw new Error(response.error?.message || 'Gagal mengambil data verifikasi')
    }
    return response.data
  },

  getVerificationDetail: async (proofId: string): Promise<PaymentProofWithRelations> => {
    const response = await api<PaymentProofWithRelations>(
      apiClient.get(`/admin/payment-proofs/${proofId}`)
    )
    if (!response.success) {
      throw new Error(response.error?.message || 'Gagal mengambil detail verifikasi')
    }
    return response.data
  },

  approveProof: async (proofId: string): Promise<void> => {
    const response = await api<null>(
      apiClient.post(`/admin/payment-proofs/${proofId}/approve`)
    )
    if (!response.success) {
      throw new Error(response.error?.message || 'Gagal menyetujui bukti pembayaran')
    }
  },

  rejectProof: async (proofId: string, reason: string): Promise<void> => {
    const response = await api<null>(
      apiClient.post(`/admin/payment-proofs/${proofId}/reject`, { reason })
    )
    if (!response.success) {
      throw new Error(response.error?.message || 'Gagal menolak bukti pembayaran')
    }
  },
}
